import Cookies from 'js-cookie'
import moment from 'moment'
import {
  BAD_REQUEST,
  CAMPAIGN_TOTAL_LEVEL,
  FORBIDDEN_ERROR,
  GENERIC_ERROR_500,
  GENERIC_ERROR_503,
  LIVE_CAMPAIGN_HEALTH_CHECK,
  MEDIA_BENCHMARKS,
  METRICS_ALREADY_EXISTS,
  METRICS_FORMULA_ERROR,
  METRIC_ALREADY_EXISTS,
  NOT_FOUND_404,
  NO_DATA,
  NO_OBJECTIVE,
  OBJECTIVE_LIST,
  POL_DOMAIN,
  SEARCH_ACTIVITY,
  SEARCH_KEYWORD_LEVEL,
  ATTRIBUTED_TRANSACTION_LOG,
  TEMPLATE_ALREADY_EXISTS,
  VIEW_ALREADY_EXISTS,
  NO_DATA_WITH_FILTER,
  PLACEMENT_LEVEL,
  TPA_REPORT_TAB,
  TPA_REPORT_CAMPAIGN_TYPES,
  DISPLAY_REPORT_CAMPAIGN_TYPES,
  MAX_RESTRICTED_REPORT_PERIOD_DAYS,
  REPORT_PERIOD_MIN_DATE_BY_TAB,
} from '../constants'
import { dateFilters, operatorOptions } from '../constants/dynamicDates'
import { AutocompleteOptions } from '@enterprise-ui/canvas-ui-react-autocomplete'
import { isLoggedInAsInternalUser } from './userAccessHelpers'
import { FormatDataResponse } from '../api/helper'
interface Option {
  id: string
  label: string
  value: string
  group?: string
}
const polDomainRegex = /\.partnersonline\.com/g

export const reportTypes: Record<string, string> = {
  MODEL_ENABLEMENT: 'Model Enablement',
  PACING_PERFORMANCE: 'Pacing/Performance',
  CAMPAIGN_DAY_LEVEL: 'Campaign Day Level',
  CAMPAIGN_REPORT_LEVEL: 'Campaign Report Level',
  CAMPAIGN_TOTAL_LEVEL: 'Campaign Total',
  ITEM_PERFORMANCE: 'Item Performance',
  CAMPAIGN_ITEM_LEVEL: 'Campaign Item Level',
  ITEM_SALES: 'Item Sales',
  MEDIA_BENCHMARKS: 'Media Benchmarks',
  LIVE_CAMPAIGN_HEALTH_CHECK: 'Live Campaign Health Check',
  SEARCH_ACTIVITY: 'Search Activity',
  SEARCH_KEYWORD_LEVEL: 'Search Keyword Level',
  ATTRIBUTED_TRANSACTION_LOG: 'Attributed Transaction Log',
  PLACEMENT_LEVEL: 'Placement Performance',
}

const getValidSubReportTypes = (type: any, list: any) => {
  for (let i of list) {
    if (i.value === type) {
      return i.label
    }
  }
  return null
}

export const getReportTypeNames = (
  types: string[] = [],
  viewType: string,
  subReportList?: any,
) => {
  let names: string[] = []
  if (types?.length > 0) {
    types.forEach((type: string) => {
      if (viewType === 'report_sub_types' && subReportList?.length > 0) {
        const getReportType = getValidSubReportTypes(type, subReportList)
        if (getReportType) {
          names.push(getReportType)
        }
      } else if (viewType === 'report_types') {
        names.push(reportTypes[type] || '')
      }
    })
    return names.join(', ')
  } else {
    return '-'
  }
}

export const validateDates = (start: string = '', end: string = '') => {
  const hasStart = !!(start && start.length > 9)
  const hasEnd = !!(end && end.length > 9)
  let endDateError = hasStart && !hasEnd
  const startDateError = hasEnd && !hasStart
  const startDateErrorMessage = 'Please select start date'
  let endDateErrorMessage = 'Please select end date'
  if (hasStart && hasEnd) {
    endDateError = new Date(start).getTime() > new Date(end).getTime()
    endDateErrorMessage = 'End date can not be prior to start date'
    if (
      new Date(new Date().setHours(0, 0, 0, 0)).getTime() >
      new Date(end).getTime()
    ) {
      endDateError =
        new Date(new Date().setHours(0, 0, 0, 0)).getTime() >
        new Date(end).getTime()
      endDateErrorMessage = 'Please select future date'
    }
  }
  return {
    startDateError,
    startDateErrorMessage,
    endDateError,
    endDateErrorMessage,
  }
}

export const validateDateForOn = (start: string = '') => {
  const hasStart = !!(start && start.length > 9)
  let startDateError = !hasStart
  let startDateErrorMessage = 'Please select start date'
  if (hasStart) {
    if (
      new Date(new Date().setHours(0, 0, 0, 0)).getTime() >
      new Date(start).getTime()
    ) {
      startDateError =
        new Date(new Date().setHours(0, 0, 0, 0)).getTime() >
        new Date(start).getTime()
      startDateErrorMessage = 'Please select future date'
    }
  }
  return {
    startDateError,
    startDateErrorMessage,
  }
}

export const stringifyParameterValues = <T extends string>(
  parameters: Parameter<T>,
): Parameter<T> => {
  const transformedParameters = {} as Parameter<T>
  for (let parameterName in parameters) {
    try {
      transformedParameters[parameterName] = JSON.stringify(
        parameters[parameterName],
      )
    } catch {
      // non serializable params are ignored
    }
  }
  return transformedParameters
}

export const isViewPage = (path: string) => {
  let isViewPage = false
  if (path.includes('/view') && !path.includes('/views')) {
    isViewPage = true
  }
  return isViewPage
}

export const parseParameterValues = <T extends string>(
  parameters: Parameter<T>,
): Parameter<T> => {
  const transformedParameters = {} as Parameter<T>
  for (let parameterName in parameters) {
    try {
      transformedParameters[parameterName] = JSON.parse(
        parameters[parameterName],
      )
    } catch {
      // non serializable params are ignored
    }
  }
  return transformedParameters
}

export const parseToJson = (param?: string) => {
  let parsed
  try {
    if (param) {
      parsed = JSON.parse(param)
    }
  } catch {
    parsed = ''
  }
  return parsed
}

export const parseTemplateData = (data: any, type: TemplateType) => {
  if (type === 'TABULAR') {
    return data.map(
      ({
        primary_dimension,
        row_dimensions,
        ...rest
      }: {
        primary_dimension?: string
        row_dimensions?: string
      }) => {
        return {
          primary_dimension: parseToJson(primary_dimension),
          row_dimensions: parseToJson(row_dimensions),
          ...rest,
        }
      },
    )
  } else {
    return data
  }
}

const mimeType: Record<string, string> = {
  'application/octet-stream': '',
  'application/zip': '.zip',
  'application/csv': '.csv',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
}

export const saveFileAs = (data: any, fileName: string, mime: string) => {
  const extension = mimeType[mime]
  const name = `${fileName}${extension}`

  const blob = new Blob([data], { type: mime || 'application/octet-stream' })
  const blobURL = window.URL.createObjectURL(blob)
  const tempLink = document.createElement('a')
  tempLink.style.display = 'none'
  tempLink.href = blobURL
  tempLink.setAttribute('download', name)

  // Safari thinks _blank anchor are pop ups. We only want to set _blank
  // target if the browser does not support the HTML5 download attribute.
  // This allows you to download files in desktop safari if pop up blocking
  // is enabled.
  if (typeof tempLink.download === 'undefined') {
    tempLink.setAttribute('target', '_blank')
  }

  document.body.appendChild(tempLink)
  tempLink.click()
  document.body.removeChild(tempLink)
  window.URL.revokeObjectURL(blobURL)
}

/* View Name Validations */
export const isValidViewLength = (
  value: string,
  maxLength: number = 100,
  minLength: number = 3,
) => {
  const val = value && value.trim()
  const length = val?.length
  return length >= minLength && length <= maxLength
}

export const getTableAutocompleteOptions = (
  obj: Record<string, any>[],
  selectedTables: string[],
) => {
  let opt: Option[] = [] as Option[]
  if (obj) {
    for (const item of obj) {
      if (![SEARCH_ACTIVITY, SEARCH_KEYWORD_LEVEL].includes(item.value)) {
        if (!selectedTables.includes(item.value)) {
          opt.push({
            id: item.value,
            value: item.value,
            label: item.label,
            group: item.isInternalOnly ? 'Internal' : '',
          })
        }
      }
    }
  }
  return opt
}

export const isPOLDomain = (hostName = '') => {
  const polDomain = hostName.match(polDomainRegex)
  return polDomain != null ? true : false
}

export const isTargetDomain = (domainName: string) => {
  const targetDomainRegex = /^target\.com$/
  return domainName.match(targetDomainRegex) != null ? true : false
}

export const isValidViewNameSearch = (value: string) => {
  const validViewNamePattern = /^[ A-Za-z0-9+|!#$,:().&_-]*$/g
  const val = value && value.trim()
  return validViewNamePattern.test(val)
}

export const isValidViewName = (value: string) => {
  return isValidViewLength(value) && isValidViewNameSearch(value)
}

/* Metric Name Validations */
export const isValidNameSearch = (value: string) => {
  const validNamePattern = /^[ A-Za-z0-9+|!#$,:().&_-]*$/g
  const val = value && value.trim()
  return validNamePattern.test(val)
}

export const isValidNumber = (value: string) => {
  const validNamePattern = /^[ 0-9+|!#$,:().&_-]*$/g
  const val = value && value.trim()
  return validNamePattern.test(val)
}

export const isValidMetricName = (value: string) => {
  return isValidViewLength(value) && isValidNameSearch(value)
}

/* Table Name Validations */
export const isValidTableLength = (value: string) => {
  const val = value && value.trim()
  const length = val.length
  return length >= 3 && length <= 31
}
export const isValidTableName = (value: string) => {
  const validTableNamePattern = /^[ A-Za-z0-9+|,-._()]*$/g
  const val = value && value.trim()
  return isValidTableLength(val) && validTableNamePattern.test(val)
}

export const validateFilterField = (
  inputValue: string,
  type: string,
  decimal?: number,
) => {
  if (inputValue === '') {
    return false
  }
  if (!inputValue) {
    return true
  }
  const val = inputValue && inputValue.trim()
  const validViewNamePattern = /^[ A-Za-z0-9+|"'/\\/!#$,:().&_-]*$/g
  if (type === 'string') {
    return validViewNamePattern.test(val)
  } else if (type === 'number') {
    const decimalFormats: string[] = [
      'percentageWithDecimal',
      'currencyWithDecimal',
      'numberWithDecimal',
    ]
    const getDecimalNumber: number =
      decimal && decimalFormats.includes(decimal.toString()) ? 2 : 0
    if (getDecimalNumber > 0) {
      return /^[0-9]\d*(\.\d+)?$/.test(val)
    } else {
      return /^\d+$/.test(val)
    }
  } else if (type === 'date') {
    return !!inputValue
  }
}

export const validateFilterFieldAll = (
  inputArray: string[],
  type: string,
  decimal?: number,
) => {
  const results: boolean[] = []
  inputArray.forEach((inputValue) => {
    var result = inputValue.replace(/[\u200B-\u200D\uFEFF]/g, '')
    results.push(!!validateFilterField(result, type, decimal))
  })
  return !results.includes(false)
}

export const getSelectedOptions = (
  selected: string[],
  responseData: SelectOption[],
) => {
  return selected.reduce((accumulator, selectedVal: string) => {
    const option = responseData.find((acct) => acct.value === selectedVal)
    if (option) {
      accumulator.push(option)
    }
    return accumulator
  }, [] as SelectOption[])
}

export const getSelectedOption = (
  value: string,
  rowOptions: SelectOption[],
) => {
  const option = rowOptions.find((options) => options.value === value)
  return option
}

export const getFilterOptions = (obj: Record<string, any>[], name: string) => {
  let opt: Option[] = [] as Option[]
  if (obj) {
    const map = new Map()
    for (const item of obj) {
      if (!map.has(item[name])) {
        map.set(item[name], true) // set any value to Map
        opt.push({
          id: item[name],
          value: item[name],
          label: item[name],
        })
      }
    }
  }
  return opt
}

export const getCustomFilterOptions = (data: string[]) => {
  let opt: Option[] = [] as Option[]
  if (data) {
    const map = new Map()
    for (const item of data) {
      //check item as API is passing null value
      if (item && !map.has(item)) {
        map.set(item, true) // set any value to Map
        opt.push({
          id: item.toString(),
          value: item.toString(),
          label: item.toString(),
        })
      }
    }
  }
  return opt
}

export const getSelectedOptionVal = (
  value: string,
  rowOptions: AutocompleteOptions[],
) => {
  const option = rowOptions.find((options) => options.value === value)
  return option
}

export const transFormPresentation = <T, K extends string>(
  presentations: Presentation<T, K>[] = [],
) =>
  presentations.map((presentation) => ({
    ...presentation,
    parameters: stringifyParameterValues(presentation.parameters),
  }))
const errorsHash: Record<string, Record<number, string>> = {
  VIEW: {
    403: FORBIDDEN_ERROR,
    409: VIEW_ALREADY_EXISTS,
    500: GENERIC_ERROR_500,
    503: GENERIC_ERROR_503,
  },
  DATA_TABLE: {
    403: FORBIDDEN_ERROR,
    404: NO_DATA,
    500: GENERIC_ERROR_500,
    503: GENERIC_ERROR_503,
  },
  VIEWS_TEMPLATE_LOAD: {
    403: FORBIDDEN_ERROR,
    500: GENERIC_ERROR_500,
    503: GENERIC_ERROR_503,
  },
  VIEW_RENAME_DUPLICATE: {
    400: BAD_REQUEST,
    403: FORBIDDEN_ERROR,
    404: NOT_FOUND_404,
    409: VIEW_ALREADY_EXISTS,
    500: GENERIC_ERROR_500,
    503: GENERIC_ERROR_503,
  },
  TEMPLATE_RENAME_DUPLICATE: {
    400: BAD_REQUEST,
    403: FORBIDDEN_ERROR,
    404: NOT_FOUND_404,
    409: TEMPLATE_ALREADY_EXISTS,
    500: GENERIC_ERROR_500,
    503: GENERIC_ERROR_503,
  },
  METRIC_RENAME_DUPLICATE: {
    400: BAD_REQUEST,
    403: FORBIDDEN_ERROR,
    404: NOT_FOUND_404,
    409: METRIC_ALREADY_EXISTS,
    500: GENERIC_ERROR_500,
    503: GENERIC_ERROR_503,
  },
  DELETE_VIEW_TEMPLATE: {
    400: BAD_REQUEST,
    403: FORBIDDEN_ERROR,
    404: NOT_FOUND_404,
    500: GENERIC_ERROR_500,
    503: GENERIC_ERROR_503,
  },
  DASHBOARD: {
    403: FORBIDDEN_ERROR,
    404: NO_DATA,
    500: GENERIC_ERROR_500,
    503: GENERIC_ERROR_503,
  },
  METRICS_LOAD: {
    403: FORBIDDEN_ERROR,
    500: GENERIC_ERROR_500,
    503: GENERIC_ERROR_503,
  },
  DIMENSIONS_LOAD: {
    403: FORBIDDEN_ERROR,
    500: GENERIC_ERROR_500,
    503: GENERIC_ERROR_503,
  },
  METRICS_CREATE: {
    403: FORBIDDEN_ERROR,
    500: GENERIC_ERROR_500,
    503: GENERIC_ERROR_503,
    400: METRICS_FORMULA_ERROR,
    409: METRICS_ALREADY_EXISTS,
  },
}

export const getErrorMessage = (
  errors: any[],
  pageOrSection: string,
  showAllErrors: boolean = true,
  showPreviewError: boolean = false,
): string => {
  let errorMessage = GENERIC_ERROR_500
  const viewErrors = errorsHash[pageOrSection]
  errorMessage = errors.reduce((message, error) => {
    const response = error?.response
    if (viewErrors[response?.status]) {
      //Error Message by status code
      if (showPreviewError && response?.status === 404) {
        message = NO_DATA_WITH_FILTER
      } else {
        message = viewErrors[response?.status]
      }
    } else if (error?.response?.data?.message && showAllErrors) {
      //Error Message from response - taken only if showAllErros is true
      message = error?.response?.data?.message
    }
    return message
  }, '')
  return errorMessage
}

export const getReportType = (reportType: any) => {
  return reportTypes[reportType]
}

export const getTemplateType = (type: string, subType?: string) => {
  const reportType = reportTypes[type]
  return subType ? `${reportType} - ${reportTypes[subType]}` : `${reportType}`
}

export const refineNewLineChar = (obj: any) => {
  let finalObj: Record<string, string> = {}
  for (let key in obj) {
    finalObj[key] = obj[key]?.replace(/\\n/g, '\n')
  }
  return finalObj
}

export const setSelectedAccount = (obj: SelectOption) => {
  setToLocalStorage('selected_vendor', obj)
}
export const setLoggedInVendorDetails = (obj: SelectOption) => {
  setToLocalStorage('external_vendor_detail', obj)
}

export const getLoggedInVendorDetail = () => {
  return getFromLocalStorage('external_vendor_detail')
}

export const getSelectedAccount = () => {
  return getFromLocalStorage('selected_vendor')
}

export const getBpIdParam = (secondParam?: string) => {
  const vendorDetails = getSelectedAccount()
  let bpIdParam = ''
  if (secondParam) {
    bpIdParam = vendorDetails?.id ? `&bp_id=${vendorDetails?.id}` : ''
  } else {
    bpIdParam = vendorDetails?.id ? `?bp_id=${vendorDetails?.id}` : ''
  }

  return bpIdParam
}

export const getVendorName = () => {
  const vendorDetails = getSelectedAccount()
  return vendorDetails?.value || ''
}

export const getExternalVendorDetails = () => {
  return getFromLocalStorage('external_vendor_detail')
}

export const getCurrentBpId = () => {
  const vendorDetails = getSelectedAccount()
  return vendorDetails?.id || ''
}

export const getExternalBpId = () => {
  const vendorDetails = getExternalVendorDetails()
  return vendorDetails?.id || ''
}

export const getUserName = () => {
  const userDir = getFromLocalStorage('userDir')
  return userDir?.userName || ''
}

export const getLoggedInUserId = () => {
  const userDir = getFromLocalStorage('userDir')
  return userDir?.lanId || ''
}

export const getUserEmail = () => {
  const userDir = getFromLocalStorage('userDir')
  return userDir?.userEmail || ''
}

export const getCompanyName = () => {
  const userDir = getFromLocalStorage('userDir')
  return userDir?.companyName || ''
}

export const getFirstName = () => {
  const userDir = getFromLocalStorage('userDir')
  return userDir?.firstName || ''
}

export const isCreatedByInternalUser = (id2Dir: string) => {
  return id2Dir === 'ad' || id2Dir === 'corp'
}

export const setToLocalStorage = (name: string, obj: Object) => {
  window.localStorage.setItem(name, JSON.stringify(obj))
}

export const getFromLocalStorage = (item: string) => {
  const localData = window.localStorage.getItem(item) as string | ''
  try {
    return JSON.parse(localData)
  } catch (err) {
    return {}
  }
}

export const removeLocalStorageItems = (removables: string[]) => {
  removables.forEach((item) => {
    localStorage.removeItem(item)
  })
}

export const getDomainName = (): string => {
  const hostName = window?.location?.hostname
  return hostName?.split('.').slice(-2).join('.')
}

export const isPOLSessionActive = (userId: string): boolean => {
  if (getDomainName() === POL_DOMAIN) {
    const ssoSessionInfo = Cookies.get('SSOSESSIONINFO') || '' //oAuth will set this cookie
    if (ssoSessionInfo) {
      const ssoSessionObj = JSON.parse(window.atob(ssoSessionInfo)) //Decode session and parse to json object
      const { user, login_status } = ssoSessionObj
      const isActiveUser = user && login_status
      if (!isActiveUser) {
        return false
      }
      if (user !== userId) {
        // when selected company changed in POL
        removeLocalStorageItems([
          'id_token',
          'access_token',
          'userDir',
          'userADGroups',
        ])
        window.location.reload()
        return true
      }
    }
  }
  return true
}

export const getSortParams = (column: string, sortOption: Sort) => {
  const { sortOrder, field } = sortOption
  let order: 'ASC' | 'DESC' | '' = ''
  let col = ''
  if (column !== field || sortOrder === '') {
    order = 'ASC'
    col = column
  } else if (sortOrder === 'ASC') {
    order = 'DESC'
    col = column
  }
  return { order, col }
}

export const getSortText = (col: string, order: string) => {
  return col && order ? `${col}-${order}` : ''
}

export const getFormattedCSTDateTime = (
  dateRefreshed: string = new Date().toString(),
) => {
  return new Date(dateRefreshed).toLocaleString('en-US', {
    timeZone: 'America/Mexico_City',
    dateStyle: 'medium',
    timeStyle: 'long',
  })
}

export const getFormattedLocalDateTime = (
  dateRefreshed: string = new Date().toString(),
) => {
  return new Date(dateRefreshed).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  })
}

export const getFormattedShortDate = (
  dateRefreshed: string = new Date().toString(),
) => {
  return new Date(dateRefreshed).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export const getUserSpecificConfig = (env: any) => {
  const commonConfig = env?.commonConfig || {}
  const internalUserConfig = env?.internalUserConfig || {}
  const externalUserConfig = env?.externalUserConfig || {}

  if (isLoggedInAsInternalUser()) {
    return Object.assign({}, commonConfig, internalUserConfig)
  } else {
    return Object.assign({}, commonConfig, externalUserConfig)
  }
}

export const getMonthlyOptions = () => {
  let options = []
  for (let i = 1; i <= 31; i++) {
    options.push({
      label: `Day ${i}`,
      value: i,
    })
  }
  return options
}

export const filterMetrics = (data: any, metricsData: Record<string, any>) => {
  const reportingType = data.reportingType
  const checkIfTableTypesMatch = (data: any) =>
    reportingType.every((type: string) =>
      data?.report_sub_types?.includes(type),
    )
  let options = []
  for (let i in metricsData) {
    if (checkIfTableTypesMatch(metricsData[i])) {
      options.push({
        id: metricsData[i]?.displayName || metricsData[i]?.metric_name,
        label: metricsData[i]?.displayName || metricsData[i]?.metric_name,
        value: i,
      })
    }
  }
  return options
}

export const getMetricsOptions = (
  metricsData: Record<string, any>,
  selectedTableType: any,
) => {
  let options: any = []
  const specificTableTypes = ['MEDIA_BENCHMARKS', 'LIVE_CAMPAIGN_HEALTH_CHECK']

  if (
    selectedTableType.length === 1 &&
    specificTableTypes.includes(selectedTableType[0])
  ) {
    metricsData = Object.values(metricsData).filter((metric: any) => {
      return metric.category !== 'Benchmarks' || !!metric.sub_category
    })
  } else {
    metricsData = Object.values(metricsData)
  }

  metricsData.forEach((metric: any) => {
    const { metric_name, field_name, sub_category } = metric
    const lable = `${metric_name}${sub_category ? ` [${sub_category}]` : ''}`
    options.push({
      id: lable,
      label: lable,
      value: field_name,
    })
  })
  return options
}

export const validateMinMax = (expr: any, char: string) => {
  let formulaArr = expr.split(' ').join('')
  let index = -1
  let regexp = /[min|max]+\([^\)]*\)(\.[^\)]*\))?/g

  while ((index = formulaArr.indexOf(char, index + 1)) !== -1) {
    if (index === -1) {
      index = 1
    }
    if (formulaArr[index + 3] !== '(') {
      return false
    }
    if (formulaArr[index + 4] === ')') {
      return false
    }
  }
  let matchFormula = formulaArr.match(regexp)
  if (matchFormula && matchFormula.length > 0) {
    for (let a = 0; a < matchFormula.length; a++) {
      let minMaxData = matchFormula[a].match(/\(([^)]+)\)/)
        ? matchFormula[a].match(/\(([^)]+)\)/)[1]
        : 0
      let funcArgs: number[] = minMaxData
        ? minMaxData.split(',').filter((s: any) => s.trim())
        : minMaxData
      if (funcArgs.length < 2) {
        return false
      }
    }
  }
  return true
}

export const isFormulaValid = (expr: string) => {
  let stack: string[] = []
  const specialChars = /[`!@#$%^&=[]{};':"\\|,.<>?~]/
  const opratorsArray = ['+', '-', '*', '/']
  let oprStack = []
  let maxExpr: any = true
  if (specialChars.test(expr)) {
    return false
  }
  if (expr.includes('min')) {
    maxExpr = validateMinMax(expr, 'min')
    if (!maxExpr) {
      return false
    }
  }
  if (expr.includes('max')) {
    maxExpr = validateMinMax(expr, 'max')
    if (!maxExpr) {
      return false
    }
  }
  if (expr.includes('(') || expr.includes(')')) {
    for (let i = 0; i <= expr.length; i++) {
      let x = expr[i]
      if (opratorsArray.includes(x)) {
        if (oprStack.length !== 0) {
          return false
        }
        oprStack.push(x)
      } else {
        if (x !== ' ') {
          oprStack = []
        }
      }
      if (x === ')' && stack.length === 0) {
        return false
      }
      if (x === '(') {
        stack.push(x)
      }
      if (x === ')') {
        stack.pop()
      }
    }
    return stack.length === 0
  } else {
    for (let i = 0; i <= expr.length; i++) {
      let x = expr[i]
      if (opratorsArray.includes(x)) {
        if (oprStack.length !== 0) {
          return false
        }
        oprStack.push(x)
      } else {
        oprStack = []
      }
    }
    return oprStack.length === 0
  }
}

export const getSortedCampaigns = (
  campaignsList: Record<string, any>[],
  sortOption: Sort,
  searchValue: string,
  type?: string,
  getDimensions?: Function,
) => {
  let finalCampaignList
  if (sortOption.field) {
    finalCampaignList = campaignsList.sort(function (a, b) {
      let currentElement =
        a[sortOption.field] !== undefined
          ? a[sortOption.field]
          : type === 'metirc'
            ? ''
            : a['flight_level_metrics'][sortOption.field]
      let nextElement =
        b[sortOption.field] !== undefined
          ? b[sortOption.field]
          : type === 'metirc'
            ? ''
            : b['flight_level_metrics'][sortOption.field]
      currentElement = Array.isArray(currentElement)
        ? currentElement.sort().toString()
        : currentElement
      nextElement = Array.isArray(nextElement)
        ? nextElement.sort().toString()
        : nextElement
      if (currentElement < nextElement) {
        return sortOption.sortOrder === 'ASC' ? -1 : 1
      }
      if (currentElement > nextElement) {
        return sortOption.sortOrder === 'ASC' ? 1 : -1
      }
      return 0
    })
  } else {
    finalCampaignList = campaignsList
  }

  const finalList = finalCampaignList.filter((item) => {
    let newItem = item
    for (const key in item) {
      if (key === 'report_sub_types' || key === 'report_types') {
        newItem = {
          ...newItem,
          [key]: getReportTypeNames(item[key], key),
        }
      } else if (key === 'report_type' || key === 'report_sub_type') {
        newItem = {
          ...newItem,
          [key]: getReportType(item[key]),
        }
      } else if (
        getDimensions &&
        (key === 'primary_dimension' || key === 'row_dimensions')
      ) {
        newItem = {
          ...newItem,
          [key]: getDimensions([item[key]] as string[]),
        }
      } else {
        newItem = {
          ...newItem,
          [key]: item[key],
        }
      }
    }
    const itemClone = Object.assign({}, newItem)
    let parsedValues = itemClone?.flight_level_metrics
      ? Object.values(itemClone?.flight_level_metrics)
      : []
    delete itemClone?.flight_level_metrics
    parsedValues = [...parsedValues, ...Object.values(itemClone)]
    return parsedValues
      .toString()
      .toLowerCase()
      .includes(searchValue.toLowerCase())
  })
  return finalList
}

export const isNotValidBenchmarkDimensions = (
  selectedDimensions: string[],
  subReportType?: string,
  overviewData?: Record<string, any>,
  isVisualization?: boolean,
  isRMSBenchmarks?: boolean,
) => {
  if (subReportType !== MEDIA_BENCHMARKS) return false

  const campaignTypes = overviewData?.campaigns_status
    ? overviewData.campaigns_status
        .map((campaign: any) => campaign.campaign_type)
        .filter((type: string) => type)
    : []

  const hasRMS = campaignTypes.includes('RMS')
  const hasManagedService = campaignTypes.includes('MANAGED_SERVICE')

  if (hasManagedService && !hasRMS) {
    return (
      mediaIdAndNameNotExists(selectedDimensions) &&
      flightNameOrProductNameNotExists(selectedDimensions) &&
      flightNameOrPLatformNameNotExists(selectedDimensions)
    )
  }

  if (hasRMS && !hasManagedService) {
    return !flightNameAndTaxonomyExists(selectedDimensions)
  }

  if (hasRMS && hasManagedService) {
    const managedServiceValid = !(
      mediaIdAndNameNotExists(selectedDimensions) &&
      flightNameOrProductNameNotExists(selectedDimensions) &&
      flightNameOrPLatformNameNotExists(selectedDimensions)
    )

    // const rmsValid = flightNameAndTaxonomyExists(selectedDimensions)
    // return !(managedServiceValid && rmsValid)
    // For External users the RMS validation is skipped, because taxonomy 1,2,3 are not valid for external users
    const rmsValid = isLoggedInAsInternalUser()
      ? flightNameAndTaxonomyExists(selectedDimensions)
      : true

    if (isVisualization) {
      if (isRMSBenchmarks) {
        return !rmsValid
      } else {
        return !managedServiceValid
      }
    }

    return !(managedServiceValid && rmsValid)
  }

  return false
}

export const isNotInKeywordByDayRange = (
  selectedDimensions: string[],
  keywordOfflineExportLimitDays: number,
  subReportType?: string,
  filters?: Filter[],
) => {
  if (subReportType !== SEARCH_KEYWORD_LEVEL) return false

  const hasEventDayDimension = selectedDimensions.includes('EVENT_DAY')
  const hasFilters = Array.isArray(filters) && filters.length > 0
  const eventDayFilter = hasFilters
    ? filters.find((filter) => filter.field === 'EVENT_DAY')
    : undefined

  if (hasEventDayDimension && !eventDayFilter) return true

  if (eventDayFilter) {
    if (
      eventDayFilter.operator === 'BETWEEN' &&
      eventDayFilter.date_selector === 'CUSTOM' &&
      eventDayFilter.value &&
      eventDayFilter.value2
    ) {
      const startDate = new Date(eventDayFilter.value)
      const endDate = new Date(eventDayFilter.value2)
      const diffDays = Math.abs(
        (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24),
      )
      if (diffDays + 1 > keywordOfflineExportLimitDays) return true
    }
    if (
      eventDayFilter.date_selector === 'CUSTOM' &&
      (eventDayFilter.operator === 'ON_OR_AFTER' ||
        eventDayFilter.operator === 'ON_OR_BEFORE')
    ) {
      return true
    }
    const longRangeSelectors = ['LAST_YEAR', 'THIS_YEAR']
    if (longRangeSelectors.includes(eventDayFilter.date_selector ?? '')) {
      return true
    }
    const presetSelector = eventDayFilter.date_selector
    if (
      eventDayFilter.operator === 'BETWEEN' &&
      presetSelector &&
      presetSelector !== 'CUSTOM' &&
      getDynamicDateRangeDays(presetSelector) > keywordOfflineExportLimitDays
    ) {
      return true
    }
    return false
  }

  return false
}

export const invalidLiveHealthCheckDimensions = (
  selectedDimensions: string[],
  subReportType?: string,
  overviewData?: Record<string, any>,
) => {
  if (subReportType !== LIVE_CAMPAIGN_HEALTH_CHECK) return false

  const campaignTypes: string[] = (overviewData?.campaigns_status ?? [])
    .map((campaign: any) => campaign.campaign_type)
    .filter(Boolean)
  const has = (key: string) => selectedDimensions.includes(key)
  const hasRMS = campaignTypes.includes('RMS')
  const hasManagedOrPBR = campaignTypes.some(
    (type) => type === 'MANAGED_SERVICE' || type === 'PBR',
  )
  const hasMediaOrFlight =
    has('MEDIA_ID') || has('MEDIA_NAME') || has('FLIGHT_NAME')
  const hasFlightAndTaxonomy = flightNameAndTaxonomyExists(selectedDimensions)

  if (hasRMS && !hasManagedOrPBR) {
    return !hasFlightAndTaxonomy
  }
  if (hasRMS && hasManagedOrPBR) {
    return !(hasMediaOrFlight && hasFlightAndTaxonomy)
  }
  return !hasMediaOrFlight
}

export const mediaIdAndNameNotExists = (selectedDimensions: string[]) => {
  const MediaIdOrMediaName = ['MEDIA_ID', 'MEDIA_NAME']
  return !selectedDimensions.some((dimension) =>
    MediaIdOrMediaName.includes(dimension),
  )
}

export const servideProviderIdAndNameNotExists = (
  selectedDimensions: string[],
) => {
  const ServiceIDorName = ['SERVICE_PROVIDER_ID', 'CAMPAIGN_PLATFORM']
  return !selectedDimensions.some((dimension) =>
    ServiceIDorName.includes(dimension),
  )
}

export const flightNameOrProductNameNotExists = (
  selectedDimensions: string[],
) => {
  const FlightNameAndProductName = ['FLIGHT_NAME', 'PRODUCT_NAME']
  return !FlightNameAndProductName.every((dimension) =>
    selectedDimensions.includes(dimension),
  )
}

export const flightNameAndTaxonomyExists = (selectedDimensions: string[]) => {
  const hasFlightName = selectedDimensions.includes('FLIGHT_NAME')
  const taxonomyFields = [
    'PRIMARY_TAXONOMY_L1',
    'PRIMARY_TAXONOMY_L2',
    'PRIMARY_TAXONOMY_L3',
  ]
  const hasAnyTaxonomy = taxonomyFields.some((taxonomy) =>
    selectedDimensions.includes(taxonomy),
  )
  return hasFlightName && hasAnyTaxonomy
}

export const flightNameOrPLatformNameNotExists = (
  selectedDimensions: string[],
) => {
  const FlightNameAndPlatformName = ['FLIGHT_NAME', 'PLATFORM_NAME']
  return !FlightNameAndPlatformName.every((dimension) =>
    selectedDimensions.includes(dimension),
  )
}

export const PlatformCampaignNameNotExists = (selectedDimensions: string[]) => {
  const hasPlatformName = selectedDimensions.includes('PLATFORM_NAME')
  const hasCampaignOrServiceProvider =
    selectedDimensions.includes('CAMPAIGN_PLATFORM') ||
    selectedDimensions.includes('SERVICE_PROVIDER_ID')
  return !(hasPlatformName && hasCampaignOrServiceProvider)
}

export const PlatformCampaignBenchmarkNotExists = (
  selectedDimensions: string[],
) => {
  const PlatformCampaignBenchmark = [
    'SERVICE_PROVIDER_ID',
    'CAMPAIGN_PLATFORM',
    'FLIGHT_NAME',
  ]
  return !PlatformCampaignBenchmark.some((dimension) =>
    selectedDimensions.includes(dimension),
  )
}

export const isNonProdEnvironment = () => {
  const nonProd = ['wip', 'dev', 'perf', 'localhost']
  const pageURL = window.location.href
  return nonProd.some((env) => {
    return pageURL.includes(env)
  })
}

export const getdynamicDatesToDisplay = (field: string, obj: any) => {
  let opt = field
  if (!obj?.date_operator) {
    return opt
  }
  const { date_operator, date_selector, date_value, date_value2 } = obj
  let operatorVal = date_operator
  let dateSelectorVal = date_selector
  operatorOptions.forEach((option) => {
    if (option.value === date_operator) {
      operatorVal = option.label
      return
    }
  })
  dateFilters.forEach((option) => {
    if (option.value === date_selector) {
      dateSelectorVal = option.label
      return
    }
  })
  if (date_operator && date_selector !== 'CUSTOM') {
    opt = `${operatorVal} - ${dateSelectorVal}`
  }
  if (date_operator && date_selector === 'CUSTOM') {
    if (date_operator === 'BETWEEN') {
      opt = `${operatorVal} - ${date_value} - ${date_value2}`
    } else {
      opt = `${operatorVal} - ${date_value}`
    }
  }
  return opt
}

export const getdynamicDateDropdownLabel = (
  field: string,
  selectedDate: any,
) => {
  const { date_operator, date_selector, date_value, date_value2 } = selectedDate
  let displayLabel = field
  let dateSelectorVal = date_selector

  if (!date_operator) {
    return displayLabel
  }

  dateFilters.forEach((option) => {
    if (option.value === date_selector) {
      dateSelectorVal = option.label
      return
    }
  })
  if (date_operator && date_selector && date_selector !== 'CUSTOM') {
    displayLabel = `${dateSelectorVal}`
  } else {
    displayLabel = 'Please select'
  }
  if (date_operator && date_selector === 'CUSTOM') {
    if (date_operator === 'BETWEEN') {
      displayLabel = `${moment(date_value).format('YYYY-MM-DD')} - ${moment(
        date_value2,
      ).format('YYYY-MM-DD')}`
    } else {
      displayLabel = `${moment(date_value).format('YYYY-MM-DD')}`
    }
  }
  return displayLabel
}

export const groupBy = (array: any, baseKey: any) => {
  return array.reduce((obj: any, key: any) => {
    ;(obj[key[baseKey]] = obj[key[baseKey]] || []).push(key)
    return obj
  }, {})
}

export const removeDuplicates = (array: any) => {
  return array.filter(
    (item: any, index: number) => array.indexOf(item) === index,
  )
}

export const isCrossAdvertiserAccount = (apiConfig: ApiConfig) => {
  const selectedAccount = getSelectedAccount()
  const externalVendorDetails = getExternalVendorDetails()
  const id = selectedAccount?.id || externalVendorDetails?.id
  return id === apiConfig?.crossAdvertiser?.carId
}

export const getReportPeriodFilter = (reportPeriod: any): Filter | null => {
  if (!reportPeriod || !reportPeriod.date_operator) {
    return null
  }

  const {
    date_operator: operator,
    date_selector,
    date_value,
    date_value2,
  } = reportPeriod

  const filter: any = {
    field: 'EVENT_DAY',
    operator,
    date_selector,
    isValid: true,
  }

  if (date_selector === 'CUSTOM') {
    filter.value = FormatDataResponse('date', date_value)
    if (operator === 'BETWEEN' && date_value2) {
      filter.value2 = FormatDataResponse('date', date_value2)
    }
  }

  return filter
}

export const getAutoReportPeriodFilter = (reportPeriod: any): Filter | null => {
  const filter = getReportPeriodFilter(reportPeriod)
  return filter ? { ...filter, autoApplied: true } : null
}

export const getReportPeriodFromCampaignOverview = (
  campaignOverviewData: any,
): any | null => {
  const overviewList = campaignOverviewData?.campaign_overview_list || []

  const startDates = overviewList
    .map((item: any) => item?.flight_start_date)
    .filter(Boolean)
    .map((date: string) => moment(date))
    .filter((date: moment.Moment) => date.isValid())
  const endDates = overviewList
    .map((item: any) => item?.flight_end_date)
    .filter(Boolean)
    .map((date: string) => moment(date))
    .filter((date: moment.Moment) => date.isValid())

  if (startDates.length === 0 || endDates.length === 0) {
    return null
  }

  const minStartDate = moment.min(startDates)
  const maxEndDate = moment.max(endDates)

  const campaignType = overviewList[0]?.campaign_type
  const isShortFlightCampaignType = ['PBR', 'RMS'].includes(campaignType)
  const endDate = isShortFlightCampaignType
    ? moment.min(
        minStartDate.clone().add(MAX_RESTRICTED_REPORT_PERIOD_DAYS - 1, 'days'),
        maxEndDate,
      )
    : campaignType === 'MANAGED_SERVICE'
      ? maxEndDate.clone().add(14, 'days')
      : maxEndDate

  return {
    date_operator: 'BETWEEN',
    date_selector: 'CUSTOM',
    date_value: minStartDate.format('YYYY-MM-DD'),
    date_value2: endDate.format('YYYY-MM-DD'),
  }
}

export const getDynamicDateRangeDays = (dateSelector: string): number => {
  const today = moment().startOf('day')
  switch (dateSelector) {
    case 'TODAY':
    case 'YESTERDAY':
      return 1
    case 'LAST_7_DAYS':
    case 'LAST_WEEK':
      return 7
    case 'LAST_14_DAYS':
    case 'LAST_2_WEEKS':
      return 14
    case 'LAST_28_DAYS':
    case 'LAST_4_WEEKS':
      return 28
    case 'LAST_30_DAYS':
      return 30
    case 'LAST_60_DAYS':
      return 60
    case 'LAST_90_DAYS':
      return 90
    case 'THIS_WEEK':
      return today.diff(moment().startOf('week'), 'days') + 1
    case 'THIS_MONTH':
      return today.diff(moment().startOf('month'), 'days') + 1
    case 'THIS_YEAR':
      return today.diff(moment().startOf('year'), 'days') + 1
    case 'LAST_MONTH':
      return moment().subtract(1, 'month').daysInMonth()
    case 'LAST_SECOND_MONTH':
      return moment().subtract(2, 'month').daysInMonth()
    case 'LAST_THIRD_MONTH':
      return moment().subtract(3, 'month').daysInMonth()
    case 'LAST_TWO_MONTHS':
      return (
        moment().subtract(1, 'month').daysInMonth() +
        moment().subtract(2, 'month').daysInMonth()
      )
    case 'LAST_QUARTER': {
      const lastQuarter = moment().subtract(1, 'quarter')
      return (
        lastQuarter
          .clone()
          .endOf('quarter')
          .diff(lastQuarter.clone().startOf('quarter'), 'days') + 1
      )
    }
    case 'LAST_YEAR':
      return moment().subtract(1, 'year').isLeapYear() ? 366 : 365
    default:
      return Infinity
  }
}

export const resolveDynamicDateRange = (
  dateSelector: string,
): { start: Date; end: Date } | null => {
  const today = moment().startOf('day')
  switch (dateSelector) {
    case 'TODAY':
      return { start: today.toDate(), end: today.toDate() }
    case 'YESTERDAY': {
      const yesterday = moment().subtract(1, 'day').startOf('day')
      return { start: yesterday.toDate(), end: yesterday.toDate() }
    }
    case 'LAST_7_DAYS':
      return {
        start: moment().subtract(6, 'days').toDate(),
        end: today.toDate(),
      }
    case 'LAST_WEEK':
    case 'LAST_2_WEEKS':
    case 'LAST_4_WEEKS': {
      const weeks = { LAST_WEEK: 1, LAST_2_WEEKS: 2, LAST_4_WEEKS: 4 }[
        dateSelector
      ]
      return {
        start: moment().subtract(weeks, 'weeks').startOf('week').toDate(),
        end: moment().subtract(1, 'week').endOf('week').toDate(),
      }
    }
    case 'LAST_14_DAYS':
      return {
        start: moment().subtract(13, 'days').toDate(),
        end: today.toDate(),
      }
    case 'LAST_28_DAYS':
      return {
        start: moment().subtract(27, 'days').toDate(),
        end: today.toDate(),
      }
    case 'LAST_30_DAYS':
      return {
        start: moment().subtract(29, 'days').toDate(),
        end: today.toDate(),
      }
    case 'LAST_60_DAYS':
      return {
        start: moment().subtract(59, 'days').toDate(),
        end: today.toDate(),
      }
    case 'LAST_90_DAYS':
      return {
        start: moment().subtract(89, 'days').toDate(),
        end: today.toDate(),
      }
    case 'THIS_WEEK':
      return { start: moment().startOf('week').toDate(), end: today.toDate() }
    case 'THIS_MONTH':
      return { start: moment().startOf('month').toDate(), end: today.toDate() }
    case 'THIS_YEAR':
      return { start: moment().startOf('year').toDate(), end: today.toDate() }
    case 'LAST_MONTH': {
      const lastMonth = moment().subtract(1, 'month')
      return {
        start: lastMonth.clone().startOf('month').toDate(),
        end: lastMonth.clone().endOf('month').toDate(),
      }
    }
    case 'LAST_SECOND_MONTH': {
      const secondLastMonth = moment().subtract(2, 'month')
      return {
        start: secondLastMonth.clone().startOf('month').toDate(),
        end: secondLastMonth.clone().endOf('month').toDate(),
      }
    }
    case 'LAST_THIRD_MONTH': {
      const thirdLastMonth = moment().subtract(3, 'month')
      return {
        start: thirdLastMonth.clone().startOf('month').toDate(),
        end: thirdLastMonth.clone().endOf('month').toDate(),
      }
    }
    case 'LAST_TWO_MONTHS':
      return {
        start: moment().subtract(2, 'month').startOf('month').toDate(),
        end: moment().subtract(1, 'month').endOf('month').toDate(),
      }
    case 'LAST_QUARTER': {
      const lastQuarter = moment().subtract(1, 'quarter')
      return {
        start: lastQuarter.clone().startOf('quarter').toDate(),
        end: lastQuarter.clone().endOf('quarter').toDate(),
      }
    }
    case 'LAST_YEAR': {
      const lastYear = moment().subtract(1, 'year')
      return {
        start: lastYear.clone().startOf('year').toDate(),
        end: lastYear.clone().endOf('year').toDate(),
      }
    }
    default:
      return null
  }
}

export const resolveReportPeriodRange = (
  reportPeriod: any,
): { start: Date | null; end: Date | null } | null => {
  if (!reportPeriod || !reportPeriod.date_operator) {
    return null
  }

  const {
    date_operator: operator,
    date_selector,
    date_value,
    date_value2,
  } = reportPeriod

  if (operator === 'BETWEEN') {
    if (date_selector === 'CUSTOM') {
      if (!date_value || !date_value2) return null
      return {
        start: moment(date_value).startOf('day').toDate(),
        end: moment(date_value2).startOf('day').toDate(),
      }
    }
    return resolveDynamicDateRange(date_selector)
  }

  const anchor: Date | null =
    date_selector === 'TODAY'
      ? moment().startOf('day').toDate()
      : date_selector === 'YESTERDAY'
        ? moment().subtract(1, 'day').startOf('day').toDate()
        : date_selector === 'CUSTOM' && date_value
          ? moment(date_value).startOf('day').toDate()
          : null

  if (!anchor) return null

  if (operator === 'ON') {
    return { start: anchor, end: anchor }
  }
  if (operator === 'ON_OR_BEFORE') {
    return { start: null, end: anchor }
  }
  if (operator === 'ON_OR_AFTER') {
    return { start: anchor, end: null }
  }
  return null
}

export const getReportPeriodRangeDays = (reportPeriod: any): number => {
  if (!reportPeriod || !reportPeriod.date_operator) {
    return Infinity
  }

  const {
    date_operator: operator,
    date_selector,
    date_value,
    date_value2,
  } = reportPeriod

  if (operator === 'ON_OR_BEFORE') {
    return Infinity
  }
  if (operator === 'ON') {
    return 1
  }
  if (date_selector === 'CUSTOM') {
    if (!date_value) return Infinity
    if (operator === 'ON_OR_AFTER') {
      return (
        moment()
          .startOf('day')
          .diff(moment(date_value).startOf('day'), 'days') + 1
      )
    }
    if (operator === 'BETWEEN') {
      if (!date_value2) return Infinity
      return Math.abs(moment(date_value2).diff(moment(date_value), 'days')) + 1
    }
    return 1
  }

  return getDynamicDateRangeDays(date_selector)
}

export const isReportPeriodWithinDayLimit = (
  reportPeriod: any,
  maxDays: number,
): boolean => {
  return getReportPeriodRangeDays(reportPeriod) <= maxDays
}

export const getEventDayFilter = (filters?: Filter[]): Filter | undefined => {
  return (filters || []).find((filter) => filter?.field === 'EVENT_DAY')
}

export const isEventDayFilterWithinDayLimit = (
  filters: Filter[] | undefined,
  maxDays: number,
): boolean => {
  const eventDayFilter = getEventDayFilter(filters)
  if (!eventDayFilter) {
    return false
  }
  const reportPeriodLike = {
    date_operator: eventDayFilter.operator,
    date_selector: eventDayFilter.date_selector,
    date_value: eventDayFilter.value,
    date_value2: eventDayFilter.value2,
  }
  return isReportPeriodWithinDayLimit(reportPeriodLike, maxDays)
}

export const isReportPeriodMissing = (
  reportType: string | undefined,
  filters: Filter[] | undefined,
  isRestrictionEnabled: boolean,
  maxDays: number,
): boolean => {
  if (
    !reportType ||
    !getRestrictedReportPeriodTypes(isRestrictionEnabled).includes(reportType)
  ) {
    return false
  }
  const eventDayFilter = getEventDayFilter(filters)
  return (
    ['ON_OR_BEFORE', 'ON_OR_AFTER'].includes(eventDayFilter?.operator || '') ||
    !isEventDayFilterWithinDayLimit(filters, maxDays)
  )
}

export const isEventDayFilterWithinReportPeriod = (
  eventDayFilter: Filter | undefined,
  reportPeriod: any,
): boolean => {
  if (!eventDayFilter) {
    return false
  }
  const existingRange = resolveReportPeriodRange({
    date_operator: eventDayFilter.operator,
    date_selector: eventDayFilter.date_selector,
    date_value: eventDayFilter.value,
    date_value2: eventDayFilter.value2,
  })
  const newRange = resolveReportPeriodRange(reportPeriod)
  if (!existingRange || !newRange) {
    return false
  }

  const startWithinRange =
    newRange.start === null ||
    (existingRange.start !== null &&
      moment(existingRange.start).isSameOrAfter(newRange.start, 'day'))
  const endWithinRange =
    newRange.end === null ||
    (existingRange.end !== null &&
      moment(existingRange.end).isSameOrBefore(newRange.end, 'day'))

  return startWithinRange && endWithinRange
}

export const getDefaultCampaignTypesForTab = (tab: string) =>
  tab === TPA_REPORT_TAB
    ? TPA_REPORT_CAMPAIGN_TYPES
    : DISPLAY_REPORT_CAMPAIGN_TYPES

export const getReportPeriodMinDateForTab = (tab: string): Date | null => {
  const minDate = REPORT_PERIOD_MIN_DATE_BY_TAB[tab]
  return minDate ? moment(minDate, 'YYYY-MM-DD').startOf('day').toDate() : null
}

export const isReportPeriodBeforeMinDate = (
  reportPeriod: any,
  minDate: Date | null,
): boolean => {
  if (!minDate) {
    return false
  }
  const range = resolveReportPeriodRange(reportPeriod)
  if (!range) {
    return false
  }
  return range.start === null || moment(range.start).isBefore(minDate, 'day')
}

const CAMPAIGN_TYPE_LABELS: Record<string, string> = {
  MANAGED_SERVICE: 'Managed Service',
  PBR: 'Programmatic by Roundel',
  RMS: 'Roundel Media Studio',
}

const toCampaignTypeOption = (value: string) => ({
  id: value,
  value,
  label: CAMPAIGN_TYPE_LABELS[value] || value,
})

export const getCampaignTypeOptionsForTab = (tab: string) =>
  getDefaultCampaignTypesForTab(tab).map(toCampaignTypeOption)

export const getAccountTypeFromCampaignType = (
  campaignType?: string,
): 'TPA' | 'DISPLAY' =>
  campaignType && TPA_REPORT_CAMPAIGN_TYPES.includes(campaignType)
    ? 'TPA'
    : 'DISPLAY'

export const getFilteredCampaigns = (campaignData: any[], filter: any) => {
  let filteredCampaigns = [...campaignData]
  if (OBJECTIVE_LIST.includes(filter?.value)) {
    filteredCampaigns = campaignData.filter((campaign: any) => {
      return (
        campaign?.objective?.toLowerCase() === filter?.value?.toLowerCase() &&
        campaign?.is_obb_campaign
      )
    })
  } else if (filter?.value === NO_OBJECTIVE) {
    filteredCampaigns = campaignData.filter(
      (campaign) => !campaign?.is_obb_campaign,
    )
  }
  return filteredCampaigns
}
export const autocompleteCustomSearch = (
  searchText: string,
  { label }: { label: string },
) => {
  const compareString = label?.toString().toLowerCase()
  searchText = searchText.toString().toLowerCase().trim()

  return compareString?.indexOf(searchText) !== -1
}

export const shouldHideField = (
  campaignFilter: any[],
  allowedValues: string[],
): boolean => {
  return (
    campaignFilter?.length > 0 &&
    !allowedValues.includes(campaignFilter[0]?.value)
  )
}

export const filterDimensionsForExternalMediaBenchmarks = (
  dimensions: string[],
  userType: string | undefined,
  reportSubType: string | undefined,
): string[] => {
  if (
    userType &&
    userType !== 'INTERNAL' &&
    reportSubType === MEDIA_BENCHMARKS
  ) {
    const dimensionsToRemove = [
      'POST_CLICK_LOOKBACK_TPA',
      'POST_VIEW_LOOKBACK_TPA',
      'PRIMARY_TAXONOMY_L1',
      'PRIMARY_TAXONOMY_L2',
      'PRIMARY_TAXONOMY_L3',
    ]
    return dimensions?.filter((dim) => !dimensionsToRemove.includes(dim))
  }
  return dimensions
}

export const shouldHideFieldForMultiSelect = (
  campaignFilter: any[],
  allowedValues: string[],
): boolean => {
  if (!campaignFilter || campaignFilter.length === 0) {
    return false
  }

  const selectedValues = campaignFilter
    .map((option: any) => option?.value)
    .filter(Boolean)

  const hasAllowedSelection = selectedValues.some((value: string) =>
    allowedValues.includes(value),
  )

  return !hasAllowedSelection
}

export const waitForElement = (
  id: string,
  timeout?: number,
  signal?: AbortSignal,
): Promise<HTMLElement> =>
  new Promise((resolve, reject) => {
    const el = document.getElementById(id)
    if (el) return resolve(el)

    const observer = new MutationObserver(() => {
      const found = document.getElementById(id)
      if (found) {
        observer.disconnect()
        resolve(found)
      }
    })

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    })

    if (signal) {
      signal.addEventListener('abort', () => {
        observer.disconnect()
        reject(new DOMException('Aborted', 'AbortError'))
      })
    }

    if (timeout != null) {
      setTimeout(() => {
        observer.disconnect()
        reject()
      }, timeout)
    }
  })

export const TOUR_PAGE_ROUTE_MAP: Record<string, RegExp[]> = {
  dashboard: [/^\/$/],
  views: [/^\/views$/],
  templates: [/^\/templates$/],
  scheduledReports: [/^\/scheduledReports$/],
  manageMetrics: [/^\/manageMetrics$/],
  report: [/^\/view$/, /^\/view\/.*$/, /^\/view\?.*$/],
}

export const doesTourApplyToRoute = (page: string, pathname: string) => {
  const patterns = TOUR_PAGE_ROUTE_MAP[page]
  if (!patterns) return false
  return patterns.some((pattern) => pattern.test(pathname))
}

export const getRestrictedReportPeriodTypes = (
  isReportPeriodRestrictionEnabled?: boolean,
): string[] => {
  return isReportPeriodRestrictionEnabled
    ? [SEARCH_ACTIVITY, SEARCH_KEYWORD_LEVEL, ATTRIBUTED_TRANSACTION_LOG]
    : []
}
